import React, { useCallback, useState } from "react";
import { BaseModal } from "../../../atoms/base-modal";
import classnames from "classnames";
import { matchType } from "../../../../application/domain/base/base-type";
import { CsvFileType, CsvUploader } from "../../../molecules/csv-uploader";
import { UserBatchUpdateResult } from "../../../../application/domain/user/user-batch-import-result";
import { BatchImportPreview } from "../../../molecules/batch-import/batch-import-preview";
import { BatchImportResult } from "../../../molecules/batch-import/batch-import-result";

type OperationState = "SELECT" | "PREVIEW" | "RESULT";

export const BatchImportUpdateUserModal: React.FC<{
  isActive: boolean;
  isBatchImporting: boolean;
  orgId: string;
  onClose: () => void;
  onImport: (
    organizationId: string,
    csv: string,
    validateOnly: boolean,
  ) => Promise<UserBatchUpdateResult | undefined>;
}> = ({ isActive, isBatchImporting, orgId, onClose, onImport }) => {
  const [pageStatus, setPageState] = useState<OperationState>("SELECT");
  const [file, setFile] = useState<CsvFileType | undefined>();
  const [result, setResult] = useState<UserBatchUpdateResult>();

  const execPreview = useCallback(async () => {
    if (!file) {
      return;
    }

    const res = await onImport(orgId, file.data, true);
    if (res) {
      setResult(res);
      setPageState("PREVIEW");
    }
  }, [onImport, orgId, file]);
  const execImport = useCallback(async () => {
    if (!file) {
      return;
    }

    const res = await onImport(orgId, file.data, false);
    if (res) {
      if (res.validateResult.isValid) setPageState("RESULT");
      setResult(res);
    }
  }, [onImport, orgId, file]);

  return (
    <BaseModal
      title={matchType(pageStatus, {
        SELECT: "ユーザー一括更新",
        PREVIEW: "一括更新内容の確認",
        RESULT: "ユーザー一括更新",
      })}
      isActive={isActive}
      onClose={onClose}
    >
      {matchType(pageStatus, {
        SELECT: (
          <>
            <section className="modal-card-body">
              <div className="mb-4">
                <p>
                  ユーザー一覧にてCSV出力を行い、ユーザー情報を編集してアップロードしてください。
                </p>
              </div>
              <CsvUploader file={file} setFile={setFile} />
            </section>
            <footer className="modal-card-foot">
              <button
                className="button is-failure is-fullwidth"
                onClick={onClose}
              >
                キャンセル
              </button>
              <button
                className={classnames("button is-info is-fullwidth")}
                onClick={execPreview}
                disabled={!file || isBatchImporting}
              >
                内容の確認に進む
              </button>
            </footer>
          </>
        ),
        PREVIEW: result && (
          <BatchImportPreview
            process="一括更新"
            file={file}
            validateResult={result.validateResult}
            execImport={execImport}
            isBatchImporting={isBatchImporting}
            returnButtonEvent={() => setPageState("SELECT")}
            doSendInvitation={false}
            orgId={orgId}
          />
        ),
        RESULT: result && (
          <BatchImportResult
            process="更新"
            validateResult={result.validateResult}
            count={result.count}
            onClose={onClose}
          />
        ),
      })}
    </BaseModal>
  );
};
